/**
 * TeacherCourseList Component
 * 
 * Displays teacher's courses in a card grid layout.
 * Shows: course name, type badge, enrolled count, average progress.
 * Follows the Instituto Valparaíso design system (dark theme, gradients, Framer Motion).
 * 
 * Requirements: 8.1, 8.2
 */

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { Users, BookOpen, TrendingUp, ChevronRight, AlertCircle } from 'lucide-react';
import CourseBadge from './CourseBadge';
import ProgressBar from './ProgressBar';

/**
 * TeacherCourseList Component
 * 
 * @param {Array} courses - Array of teacher's courses with stats
 * @param {boolean} loading - Whether data is being loaded
 * @param {string} error - Error message if data fetch failed
 * @param {Function} onCourseSelect - Callback when a course is selected
 * @param {number} selectedCourseId - Currently selected course ID
 * @param {Function} onRefresh - Callback to refresh course data
 * @param {string} className - Additional CSS classes
 */
export default function TeacherCourseList({
  courses = [],
  loading = false,
  error = null,
  onCourseSelect = null,
  selectedCourseId = null,
  onRefresh = null,
  className = '',
}) {
  // Animation variants for stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  /**
   * Handle course card click
   */
  const handleCourseClick = (course) => {
    if (onCourseSelect) {
      onCourseSelect(course);
    }
  };

  return (
    <div className={`${className}`}>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-400" />
          Mis Cursos
        </h3>
        {onRefresh && !loading && (
          <motion.button
            onClick={onRefresh}
            className="px-4 py-2 text-sm text-white/60 hover:text-white
                       bg-white/5 hover:bg-white/10 rounded-lg
                       border border-white/10 hover:border-white/20
                       transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Actualizar
          </motion.button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4" />
          <p className="text-white/60 text-sm">Cargando cursos...</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-3 rounded-full bg-red-500/20 mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">
            Error al cargar los cursos
          </h4>
          <p className="text-white/60 text-sm mb-4 max-w-sm">{error}</p>
          {onRefresh && (
            <motion.button
              onClick={onRefresh}
              className="px-5 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 
                         hover:from-blue-600 hover:to-cyan-600
                         text-white font-medium rounded-lg text-sm
                         shadow-lg shadow-blue-500/30 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Reintentar
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && !error && courses.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-3 rounded-full bg-blue-500/20 mb-4">
            <BookOpen className="w-6 h-6 text-blue-400" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">
            No tienes cursos asignados
          </h4>
          <p className="text-white/60 text-sm max-w-sm">
            Contacta al administrador si crees que deberías tener cursos asignados.
          </p>
        </motion.div>
      )}

      {/* Course Grid */}
      {!loading && !error && courses.length > 0 && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {courses.map((course) => (
            <motion.div
              key={course.id}
              variants={itemVariants}
              onClick={() => handleCourseClick(course)}
              className={`
                p-5 rounded-xl cursor-pointer
                bg-white/5 border backdrop-blur-sm
                transition-all duration-300
                ${selectedCourseId === course.id
                  ? 'border-blue-500/50 bg-blue-500/10 ring-1 ring-blue-500/30'
                  : 'border-white/10 hover:bg-white/10 hover:border-white/20'
                }
              `}
              whileHover={{ y: -4, scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {/* Course Header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <h4 className="text-lg font-semibold text-white line-clamp-2 flex-1">
                  {course.name}
                </h4>
                <CourseBadge type={course.type} size="sm" />
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Enrolled Count */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                  <Users className="w-4 h-4 text-cyan-400" />
                  <div>
                    <p className="text-xs text-white/50">Inscritos</p>
                    <p className="text-sm font-semibold text-white">
                      {course.enrolledCount || 0}
                    </p>
                  </div>
                </div>

                {/* Average Progress */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <div>
                    <p className="text-xs text-white/50">Promedio</p>
                    <p className="text-sm font-semibold text-white">
                      {Math.round(course.averageProgress || 0)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <p className="text-xs text-white/50 mb-1">Progreso promedio</p>
                <ProgressBar 
                  progress={course.averageProgress || 0} 
                  size="sm" 
                  showPercentage={false}
                />
              </div>

              {/* View Details Link */}
              <div className="flex items-center justify-end">
                <span className="text-xs text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Ver detalles
                  <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

TeacherCourseList.propTypes = {
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['synchronous', 'asynchronous']).isRequired,
      enrolledCount: PropTypes.number,
      averageProgress: PropTypes.number,
      meetingLink: PropTypes.string,
      meetingLinkUpdatedAt: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onCourseSelect: PropTypes.func,
  selectedCourseId: PropTypes.number,
  onRefresh: PropTypes.func,
  className: PropTypes.string,
};
